import { MenuCategory, MenuCategoryData } from './index';
import { ReadCollectionGql } from './gql';

type Data = {
  documents: MenuCategoryData[];
  nextToken: string;
};

class MenuCategoryCollection {

  readonly businessId: string;
  readonly documents: MenuCategory[];
  readonly nextToken: string;

  constructor(businessId: string = '', data: Data = { documents: [], nextToken: '' }) {
    this.businessId = businessId;
    this.documents = data.documents.map(it => new MenuCategory(it));
    this.nextToken = data.nextToken;
  }

  async readAll(): Promise<this> {
    let collection = this;
    while (collection.nextToken) {
      const { businessId, nextToken } = collection;
      if (!businessId) continue;
      const gql = await new ReadCollectionGql().fetch({ businessId, nextToken });
      if (!gql.result) throw new Error('invalid result');
      collection = collection.merge(new MenuCategoryCollection(businessId, gql.result));
    }
    return collection;
  }

  merge(collection: MenuCategoryCollection): this {
    const documents = MenuCategory.sort([...this.documents, ...collection.documents]);
    const nextToken = collection.nextToken;
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents, nextToken });
  }

  add(category: MenuCategory): this {
    const documents = MenuCategory.sort([...this.documents, category]);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  replace(category: MenuCategory): this {
    const documents = MenuCategory.sort(this.documents.map(it => it.id === category.id ? category : it));
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  remove(category: MenuCategory): this {
    const documents = this.documents.filter(it => it.id !== category.id);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  toJSON(): Data {
    const { nextToken } = this;
    const documents = this.documents.map(it => it.toJSON());
    return { documents, nextToken };
  }

  static async read(businessId: string): Promise<MenuCategoryCollection> {
    const gql = await new ReadCollectionGql().fetch({ businessId });
    if (!gql.result) throw new Error('invalid result');
    return new MenuCategoryCollection(businessId, gql.result);
  }

}

export { MenuCategoryCollection };
export type { Data as MenuCategoryCollectionData };